// CR-0074: sessions-R02/R19/R21; access-and-auth-login-sessions-R09/R10.
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { ElMessage } from 'element-plus';
import { authApi } from '@/shared/api';
import type { IAuthUser } from '@/shared/api/auth';
import { advanceAuthSession, getAuthSessionGeneration } from '@/shared/api/client';
import { hasPrivilege as allows, hasAllPrivileges as allowsAll, hasAnyPrivilege as allowsAny, canAccessSection as allowsSection, EDIT_SESSION_PRIVILEGES, type TPrivilege, type TSection } from '@/shared/config/privileges';
import { setAuthenticationFailureHandler } from '@/shared/api/client';
import { i18n } from '@/shared/i18n';
function isAuthenticationRefusal(error: unknown): boolean {
  if (error === null || typeof error !== 'object' || !('response' in error)) return false;
  const response: unknown = error.response;
  return response !== null && typeof response === 'object' && 'status' in response && response.status === 401;
}
export const useAuthStore = defineStore('auth', () => {
  const user = ref<IAuthUser | null>(null);
  const privileges = ref<readonly string[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const isAuthenticated = computed(() => user.value !== null);
  const currentUser = computed(() => user.value);
  const currentUserLogin = computed(() => user.value?.login ?? '');
  function hasPrivilege(code: TPrivilege): boolean { return isAuthenticated.value && allows(privileges.value, code); }
  function hasAllPrivileges(codes: readonly TPrivilege[]): boolean { return isAuthenticated.value && allowsAll(privileges.value, codes); }
  function hasAnyPrivilege(codes: readonly TPrivilege[]): boolean { return isAuthenticated.value && allowsAny(privileges.value, codes); }
  function canAccessSection(section: TSection): boolean { return isAuthenticated.value && allowsSection(privileges.value, section); }
  const canOpenEditSession = computed(() => hasAnyPrivilege(EDIT_SESSION_PRIVILEGES));
  function clearSession(): void {
    advanceAuthSession(); user.value = null; privileges.value = []; isLoading.value = false; error.value = null;
    localStorage.removeItem('accessToken'); localStorage.removeItem('refreshToken');
  }
  setAuthenticationFailureHandler(clearSession);
  function startLogin(returnTo: string = '/'): void { authApi.startLogin(returnTo); }
  async function logout(): Promise<void> {
    clearSession();
    authApi.logout();
  }
  async function loadProfile(): Promise<boolean> {
    const generation = getAuthSessionGeneration();
    isLoading.value = true;
    try {
      const [profile, grants] = await Promise.all([authApi.getCurrentUser(), authApi.getPermissions()]);
      if (generation !== getAuthSessionGeneration()) return false;
      user.value = profile; privileges.value = grants; error.value = null;
      return true;
    } catch (failure: unknown) {
      if (generation !== getAuthSessionGeneration()) return false;
      if (isAuthenticationRefusal(failure)) { clearSession(); return false; }
      error.value = i18n.global.t('auth.profileLoadFailed');
      ElMessage.error(error.value);
      throw failure;
    } finally { if (generation === getAuthSessionGeneration()) isLoading.value = false; }
  }
  async function initializeAuth(): Promise<boolean> {
    advanceAuthSession();
    // Retired local tokens are never read or sent.
    localStorage.removeItem('accessToken'); localStorage.removeItem('refreshToken');
    return loadProfile();
  }
  async function refreshUser(): Promise<void> {
    if (user.value === null) return;
    const restored = await loadProfile().catch(() => true);
    if (!restored) startLogin(`${window.location.pathname}${window.location.search}${window.location.hash}`);
  }
  return { user, privileges, isLoading, error, isAuthenticated, currentUser, currentUserLogin,
    hasPrivilege, hasAllPrivileges, hasAnyPrivilege, canAccessSection, canOpenEditSession, startLogin, logout, refreshUser, initializeAuth };
});
